import React, { createContext, useContext } from 'react';
import { useClientForm, ClientFormData } from '../hooks/useClientForm';

interface ClientFormContextType {
  formData: ClientFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  updateFormData: (updates: Partial<ClientFormData>) => void;
}

const ClientFormContext = createContext<ClientFormContextType | undefined>(undefined);

interface ClientFormProviderProps {
  children: React.ReactNode;
  initialData?: any;
}

export const ClientFormProvider: React.FC<ClientFormProviderProps> = ({ children, initialData }) => {
  const transformedData = initialData ? {
    civility: initialData.civility || '',
    firstName: initialData.firstName || initialData.first_name || '',
    lastName: initialData.lastName || initialData.last_name || '',
    phone: initialData.phone || '',
    email: initialData.email || '',
    address: initialData.address || '',
    wilaya: initialData.wilaya || '',
    city: initialData.city || '',
    propertyType: initialData.criteria?.propertyType || initialData.property_type || '',
    transactionType: initialData.criteria?.transactionType || initialData.transaction_type || '',
    minBudget: (initialData.criteria?.minBudget || initialData.min_budget || '').toString(),
    maxBudget: (initialData.criteria?.maxBudget || initialData.max_budget || '').toString(),
    minArea: (initialData.criteria?.minArea || initialData.min_area || '').toString(),
    minRooms: (initialData.criteria?.minRooms || initialData.min_rooms || '').toString(),
    criteria: typeof initialData.criteria === 'object' && initialData.criteria !== null
      ? Object.keys(initialData.criteria).reduce((acc, key) => {
          if (!['transactionType', 'propertyType', 'minBudget', 'maxBudget', 'minArea', 'minRooms'].includes(key)) {
            acc[key] = initialData.criteria[key];
          }
          return acc;
        }, {} as Record<string, boolean>)
      : {},
    notes: initialData.notes || '',
    agent: initialData.agent_id?.toString() || initialData.agent || '',
    interestLevel: initialData.interestLevel || initialData.interest_level || 'interested',
  } : undefined;

  const formMethods = useClientForm(transformedData);

  return (
    <ClientFormContext.Provider value={formMethods}>
      {children}
    </ClientFormContext.Provider>
  );
};

export const useClientFormContext = () => {
  const context = useContext(ClientFormContext);
  if (!context) {
    throw new Error('useClientFormContext must be used within a ClientFormProvider');
  }
  return context;
};